/**
 * Named gesture-tuning presets.
 *
 * Centralising the magic numbers (deadzone, rubber-band, fling, commit
 * thresholds, animation durations) keeps the feel consistent across call sites
 * and makes tuning a one-line change. Each preset bundles the recognizer's
 * tuning with the consumer-facing layout constants the recognizer does not own.
 */

const GESTURE_DEADZONE_PX = 8;

const STANDARD_FLING = {
  maxDurationMs: 300,
  minDistancePx: 50,
  axisRatio: 2,
} as const;

/**
 * Toast swipe-to-dismiss (X). Single-axis so it never fights vertical page
 * scroll; the card follows the finger in either direction and is dismissed past
 * a fraction of its width or on a fling, otherwise snaps back. Free tracking (no
 * rubber-band clamp) since the card leaves the screen on commit.
 */
export const TOAST_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 80,
  commitThresholdRatio: 0.4,
  rubberBandLimitPx: 9999,
  rubberBandFactor: 1,
  hapticMs: 12,
  commitAnimationMs: 220,
  cancelAnimationMs: 220,
  fallbackWidthPx: 400,
  fling: { ...STANDARD_FLING },
} as const;

/**
 * Mobile navigation drawer: drag it toward its own edge to dismiss it.
 *
 * The drawer is anchored to the left, so only a leftward drag means anything.
 * Dragging the other way has nothing to reveal — it gets a short, capped
 * rubber-band so the panel acknowledges the finger without ever detaching from
 * its edge or committing anything.
 */
export const NAV_DRAWER_DISMISS = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Travel toward the edge that commits the close on release. */
  commitThresholdPx: 72,
  /** Give on the closed-direction drag (the one that does nothing). */
  rubberBandFactor: 0.25,
  /** Hard cap on that give. */
  rubberBandLimitPx: 24,
  hapticMs: 12,
  /** Used for the backdrop fade when the panel has not been measured yet. */
  fallbackWidthPx: 280,
  /** A flick dismisses without reaching `commitThresholdPx`. */
  fling: { maxDurationMs: 300, minDistancePx: 40, axisRatio: 2 },
} as const;

/** Per-row swipe-to-action (archive / delete), matching the canon todo-row feel. */
export const ROW_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 100,
  rubberBandLimitPx: 130,
  rubberBandFactor: 0.25,
  hapticMs: 15,
  commitAnimationMs: 300,
  cancelAnimationMs: 240,
  fallbackWidthPx: 400,
} as const;

/** Per-row swipe-to-delete (single direction), reused by the detail-view lists. */
export const ROW_SWIPE_DELETE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 96,
  rubberBandLimitPx: 120,
  rubberBandFactor: 0.25,
  hapticMs: 15,
  commitAnimationMs: 260,
  cancelAnimationMs: 220,
  fallbackWidthPx: 400,
} as const;

/**
 * Full-surface horizontal carousel (prev / next), matching the canon drawer
 * navigation feel: a longer commit throw, a soft fling, and a gentle rubber-band
 * at the ends of the collection. Distinct from `ROW_SWIPE` so a wide content
 * surface is not over-eager to commit on a small drag.
 */
export const DRAWER_CAROUSEL = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 72,
  rubberBandLimitPx: 80,
  rubberBandFactor: 0.2,
  /** Past-the-end rubber-band uses a stiffer factor so dead ends feel bounded. */
  edgeRubberBandFactor: 0.12,
  hapticMs: 12,
  commitAnimationMs: 240,
  cancelAnimationMs: 220,
  fallbackWidthPx: 400,
  fling: {
    maxDurationMs: 300,
    minDistancePx: 40,
    axisRatio: 1.2,
  },
} as const;
